import ReactQuill from 'react-quill';

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'] as const;
export type QuillFontSize = (typeof FONT_SIZES)[number];

export const DEFAULT_FONT_SIZE: QuillFontSize = '18px';

export const FONT_SIZE_OPTIONS: Array<{ label: string; value: QuillFontSize }> = FONT_SIZES.map((v) => ({
  label: v.replace('px', ''),
  value: v,
}));

let didRegister = false;

export function ensureQuillFontSizesRegistered(): void {
  if (didRegister) return;

  const Quill = (ReactQuill as any).Quill;
  if (!Quill) {
    // ReactQuill didn't expose Quill yet; try again on next import.
    return;
  }

  try {
    const SizeStyle = Quill.import('attributors/style/size');
    if (SizeStyle) {
      SizeStyle.whitelist = [...FONT_SIZES];
      Quill.register(SizeStyle, true);
    }
    didRegister = true;
  } catch {
    // Ignore: Quill internals not available or already registered.
    didRegister = true;
  }
}

export function normalizeQuillSizeFormat(size: unknown): QuillFontSize {
  const s = String(size ?? '').trim();
  if ((FONT_SIZES as readonly string[]).includes(s)) return s as QuillFontSize;
  return DEFAULT_FONT_SIZE;
}

export function stepQuillFontSize(currentSize: unknown, delta: -1 | 1): QuillFontSize {
  const cur = normalizeQuillSizeFormat(currentSize);
  const idx = FONT_SIZES.indexOf(cur);
  if (idx < 0) return DEFAULT_FONT_SIZE;
  const nextIdx = Math.max(0, Math.min(FONT_SIZES.length - 1, idx + delta));
  return FONT_SIZES[nextIdx];
}

export function applyQuillFontSizeStep(quill: any, delta: -1 | 1): void {
  if (!quill || typeof quill.getSelection !== 'function' || typeof quill.getFormat !== 'function') return;

  const range = quill.getSelection(true);
  if (!range) return;

  const format = quill.getFormat(range);
  const next = stepQuillFontSize(format?.size, delta);
  quill.format('size', next, 'user');
}
